import { useState } from 'react'
import { Modal } from 'grabbitbot-legacy-ui'
import { plans, products } from '../api/fee'
import type { Plan, Product } from '../api/types'
import { CURRENCIES, planLimits } from '../lib/catalogue'
import { useMoneyAction } from '../lib/useMoneyAction'
import { Notice } from './common'

/** A decimal typed by the admin, validated as a STRING and sent as typed. */
const PRICE = /^\d+(?:\.\d+)?$/

type Kind = 'plan' | 'product'

/**
 * Add / Edit modal for a plan or product (legacy plan-list / product-list /
 * admin-plan-list / admin-product-list inline modals — B3b, admin only).
 * Fields: Name, Price (major units, sent as the typed string), Currency,
 * "Visible to anyone" (the legacy INVERTED select: value 1 = hidden = "No",
 * value 0 = "Yes"), a product's payable-anytime/once `limit`, and on EDIT of
 * a plan the limits table (negative value = "doesn't apply to this plan").
 */
export function CatalogueEditor({
  kind,
  projectId,
  item,
  onClose,
  onSaved,
}: {
  kind: Kind
  projectId: string
  item: Plan | Product | null
  onClose: () => void
  onSaved: () => void
}) {
  const isNew = !item
  const [name, setName] = useState(item?.name ?? '')
  const [price, setPrice] = useState(item ? String(item.price ?? '') : '')
  const [currency, setCurrency] = useState<string>(item?.currency ?? 'usd')
  const [hidden, setHidden] = useState<number>(item?.hidden ? 1 : 0)
  const [limit, setLimit] = useState<number>(kind === 'product' ? Number((item as Product | null)?.limit ?? 0) : 0)
  const [limits, setLimits] = useState(() =>
    kind === 'plan' && item ? planLimits(item as Plan).map((l) => ({ ...l, value: String(l.value) })) : [],
  )
  const actions = useMoneyAction()

  const save = async () => {
    const p = price.trim()
    if (!name.trim()) return actions.setFeedback('save', { tone: 'warning', text: 'Enter a name.' })
    if (!PRICE.test(p)) return actions.setFeedback('save', { tone: 'warning', text: 'Enter a valid price (e.g. 19.99).' })
    const body: Record<string, unknown> = { name: name.trim(), price: p, currency, hidden }
    if (kind === 'product') body.limit = limit
    if (kind === 'plan' && !isNew) {
      for (const l of limits) {
        if (!/^-?\d+$/.test(l.value.trim())) {
          return actions.setFeedback('save', { tone: 'warning', text: `Limit "${l.key}" must be a whole number.` })
        }
      }
      body.limits = limits.map((l) => ({ key: l.key, value: l.value.trim() }))
    }
    const api = kind === 'plan' ? plans : products
    const result = await actions.run('save', JSON.stringify(body), () =>
      isNew ? api.add(projectId, body) : api.modify(projectId, item!.id, body),
    )
    if (result) onSaved()
  }

  const title = `${isNew ? 'Add' : 'Edit'} ${kind}`
  return (
    <Modal open title={title} onClose={onClose} className="portal-modal-wide">
      <form
        className="portal-form"
        aria-label={title}
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          Price
          <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" />
        </label>
        <label>
          Currency
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c.toUpperCase()}
              </option>
            ))}
          </select>
        </label>
        {kind === 'product' && (
          <label>
            Payable
            <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
              <option value={0}>payable anytime</option>
              <option value={1}>once payable</option>
            </select>
          </label>
        )}
        <label>
          Visible to anyone
          <select value={hidden} onChange={(e) => setHidden(Number(e.target.value))}>
            <option value={1}>No (only with link or previous subscription)</option>
            <option value={0}>Yes</option>
          </select>
        </label>
        {kind === 'plan' && !isNew && limits.length > 0 && (
          <fieldset className="fs-fieldset">
            <legend>Limits</legend>
            <p className="portal-hint">Enter a negative value to every limits, which doesn&apos;t apply to the given plan.</p>
            {limits.map((l, i) => (
              <label key={l.key} className={Number(l.value) < 0 ? 'fs-limit fs-limit--off' : 'fs-limit'}>
                {l.key}
                {l.description ? ` — ${l.description}` : ''}
                <input
                  inputMode="numeric"
                  value={l.value}
                  onChange={(e) => {
                    const next = [...limits]
                    next[i] = { ...l, value: e.target.value }
                    setLimits(next)
                  }}
                />
              </label>
            ))}
          </fieldset>
        )}
        <div className="portal-inline-actions">
          <button type="submit" disabled={actions.isBusy('save')}>
            {actions.isBusy('save') ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
        <Notice feedback={actions.feedbackFor('save')} />
      </form>
    </Modal>
  )
}
